import { AlertTriangle } from "lucide-react";
import { useEffect } from "react";

const Disclaimers = () => {
  // Set page title
  useEffect(() => {
    document.title = "Disclaimers - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  return (
    <div className="legal-page" style={{ color: '#000000' }}>
      <div className="legal-container">
        <div className="legal-content" style={{ color: '#000000' }}>
          <h1 className="legal-title gold-text">Disclaimers</h1>
          <p className="legal-updated">Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="disclaimer-banner">
            <AlertTriangle size={32} className="gold-icon" />
            <p className="disclaimer-main">
              <strong>CRITICAL DISCLAIMER:</strong> AI for Law Firms and its AI assistant Lawra DO NOT provide legal advice, 
              legal opinions, or legal representation of any kind.
            </p>
          </div>

          <section className="legal-section">
            <h2>1. No Legal Advice</h2>
            <p className="legal-important">
              <strong>This Platform is NOT a substitute for legal advice from a qualified attorney.</strong>
            </p>
            <p>
              All content, analyses, documents, and information provided by the Platform, including outputs from Lawra, 
              are for general informational and educational purposes only. They do not constitute legal advice and should 
              not be relied upon as such.
            </p>
            <p>
              Every legal situation is unique. Only a licensed attorney who is familiar with the specific facts and 
              circumstances of your case can provide appropriate legal advice. You should always consult with a qualified 
              legal professional before making any legal decisions.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. No Attorney-Client Relationship</h2>
            <p>
              Use of this Platform does NOT create an attorney-client relationship between you and AI for Law Firms, 
              its employees, contractors, or any third parties. No attorney-client privilege applies to any information 
              you submit or receive through the Platform.
            </p>
            <p>
              Communications with Lawra and use of the Platform's features do not constitute confidential communications 
              protected by attorney-client privilege.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. AI Technology Limitations</h2>
            <p>
              The Platform uses artificial intelligence technology, which has inherent limitations:
            </p>
            <ul>
              <li><strong>Accuracy:</strong> AI-generated content may contain errors, inaccuracies, or omissions</li>
              <li><strong>Currency:</strong> AI knowledge may not reflect the most recent legal developments or changes in law</li>
              <li><strong>Jurisdiction:</strong> AI may not accurately account for jurisdiction-specific laws and procedures</li>
              <li><strong>Context:</strong> AI cannot fully understand the nuanced context of individual legal situations</li>
              <li><strong>Judgment:</strong> AI lacks human judgment, wisdom, and legal expertise</li>
            </ul>
            <p className="legal-important">
              <strong>Always verify AI-generated content with qualified legal professionals before relying on it.</strong>
            </p>
          </section>

          <section className="legal-section">
            <h2>4. No Warranty or Guarantee</h2>
            <p>
              The Platform and all content are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, 
              either express or implied, including but not limited to:
            </p>
            <ul>
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties of accuracy, completeness, or reliability</li>
              <li>Warranties that the Platform will be uninterrupted or error-free</li>
              <li>Warranties regarding results or outcomes</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Document Analysis Disclaimer</h2>
            <p>
              Document review, contract analysis, and due diligence features are automated tools that:
            </p>
            <ul>
              <li>May not identify all relevant issues, risks, or clauses</li>
              <li>May produce false positives or miss important information</li>
              <li>Should never be used as the sole basis for legal decisions</li>
              <li>Require review and verification by qualified legal professionals</li>
            </ul>
            <p>
              Always have important documents reviewed by a licensed attorney before making decisions based on them.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Litigation and Deposition Tools Disclaimer</h2>
            <p>
              Tools designed for litigation preparation, including deposition and witness preparation features:
            </p>
            <ul>
              <li>Are aids for legal professionals, not replacements for legal expertise</li>
              <li>May not identify all inconsistencies or strategic opportunities</li>
              <li>Should be used in conjunction with thorough legal analysis by qualified attorneys</li>
              <li>Cannot account for witness demeanor, credibility factors, or courtroom dynamics</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Confidentiality Warning</h2>
            <p className="legal-important">
              <strong>DO NOT upload privileged, confidential, or highly sensitive documents</strong> unless you understand 
              and accept the risks.
            </p>
            <p>
              While we implement security measures, no system is completely secure. Documents uploaded to the Platform 
              are processed by third-party AI services. You are responsible for assessing whether uploading specific 
              documents is appropriate for your situation.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Limitation of Liability</h2>
            <p>
              AI for Law Firms, its officers, directors, employees, agents, and affiliates shall not be liable for any 
              damages arising from:
            </p>
            <ul>
              <li>Reliance on information provided by the Platform</li>
              <li>Errors, omissions, or inaccuracies in AI-generated content</li>
              <li>Decisions made based on Platform outputs</li>
              <li>Legal consequences of actions taken or not taken</li>
              <li>Loss of data, documents, or information</li>
            </ul>
            <p>
              To the maximum extent permitted by law, our total liability is limited to the amount you paid for the Platform 
              in the 12 months preceding the claim.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Jurisdiction-Specific Limitations</h2>
            <p>
              Legal requirements vary by jurisdiction. The Platform provides general information that may not be applicable 
              to your specific jurisdiction. Always consult with an attorney licensed to practice in your jurisdiction.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Professional Responsibility</h2>
            <p>
              If you are a legal professional using this Platform:
            </p>
            <ul>
              <li>You remain responsible for exercising independent professional judgment</li>
              <li>You must comply with all applicable rules of professional conduct</li>
              <li>You are responsible for supervising and verifying all AI-generated content</li>
              <li>You should not delegate professional responsibilities to AI tools</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Emergency Situations</h2>
            <p className="legal-important">
              <strong>This Platform is not designed for emergency situations.</strong>
            </p>
            <p>
              If you are facing an immediate legal emergency, urgent deadline, or time-sensitive legal matter, 
              contact a qualified attorney immediately. Do not rely on this Platform for urgent legal needs.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. External Links</h2>
            <p>
              The Platform may contain links to external websites or resources. We are not responsible for the content, 
              accuracy, or practices of third-party sites. Links do not constitute endorsements.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Acknowledgment</h2>
            <p>
              By using the Platform, you acknowledge that you have read, understood, and agree to be bound by these 
              Disclaimers. You understand that:
            </p>
            <ul>
              <li>The Platform does not provide legal advice</li>
              <li>No attorney-client relationship is created</li>
              <li>AI-generated content may contain errors</li>
              <li>You should consult with a qualified attorney for legal advice</li>
              <li>You use the Platform at your own risk</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>14. Contact for Legal Advice</h2>
            <p>
              If you need legal advice, please contact a licensed attorney in your jurisdiction. You can typically find 
              attorneys through:
            </p>
            <ul>
              <li>Your local bar association</li>
              <li>Legal referral services</li>
              <li>Legal aid organizations (for those who qualify)</li>
              <li>Professional legal directories</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Disclaimers;
